/**
=========================================================
* Material Dashboard 2 PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from '@mui/material';

// Material Dashboard 2 PRO React TS Base Styles
import colors from 'src/assets/md-theme/base/colors';
import breakpoints from 'src/assets/md-theme/base/breakpoints';
import typography from 'src/assets/md-theme/base/typography';
import boxShadows from 'src/assets/md-theme/base/boxShadows';
import borders from 'src/assets/md-theme/base/borders';
import globals from 'src/assets/md-theme/base/globals';

// Material Dashboard 2 PRO React TS Helper Functions
import boxShadow from 'src/assets/md-theme/functions/boxShadow';
import hexToRgb from 'src/assets/md-theme/functions/hexToRgb';
import linearGradient from 'src/assets/md-theme/functions/linearGradient';
import pxToRem from 'src/assets/md-theme/functions/pxToRem';
import rgba from 'src/assets/md-theme/functions/rgba';

// Material Dashboard 2 PRO React TS components base styles for @mui material components
import sidenav from 'src/assets/md-theme/components/sidenav';
import list from 'src/assets/md-theme/components/list';
import listItem from 'src/assets/md-theme/components/list/listItem';
import listItemText from 'src/assets/md-theme/components/list/listItemText';
import card from 'src/assets/md-theme/components/card';
import cardMedia from 'src/assets/md-theme/components/card/cardMedia';
import cardContent from 'src/assets/md-theme/components/card/cardContent';
import button from 'src/assets/md-theme/components/button';
import iconButton from 'src/assets/md-theme/components/iconButton';
import input from 'src/assets/md-theme/components/form/input';
import inputLabel from 'src/assets/md-theme/components/form/inputLabel';
import inputOutlined from 'src/assets/md-theme/components/form/inputOutlined';
import textField from 'src/assets/md-theme/components/form/textField';
import menu from 'src/assets/md-theme/components/menu';
import menuItem from 'src/assets/md-theme/components/menu/menuItem';
import switchButton from 'src/assets/md-theme/components/form/switchButton';
import divider from 'src/assets/md-theme/components/divider';
import tableContainer from 'src/assets/md-theme/components/table/tableContainer';
import tableHead from 'src/assets/md-theme/components/table/tableHead';
import tableCell from 'src/assets/md-theme/components/table/tableCell';
import tableRow from 'src/assets/md-theme/components/table/tableRow';
import linearProgress from 'src/assets/md-theme/components/linearProgress';
import breadcrumbs from 'src/assets/md-theme/components/breadcrumbs';
import slider from 'src/assets/md-theme/components/slider';
import avatar from 'src/assets/md-theme/components/avatar';
import tooltip from 'src/assets/md-theme/components/tooltip';
import appBar from 'src/assets/md-theme/components/appBar';
import tabs from 'src/assets/md-theme/components/tabs';
import tab from 'src/assets/md-theme/components/tabs/tab';
import stepper from 'src/assets/md-theme/components/stepper';
import step from 'src/assets/md-theme/components/stepper/step';
import stepConnector from 'src/assets/md-theme/components/stepper/stepConnector';
import stepLabel from 'src/assets/md-theme/components/stepper/stepLabel';
import stepIcon from 'src/assets/md-theme/components/stepper/stepIcon';
import select from 'src/assets/md-theme/components/form/select';
import formControlLabel from 'src/assets/md-theme/components/form/formControlLabel';
import formLabel from 'src/assets/md-theme/components/form/formLabel';
import checkbox from 'src/assets/md-theme/components/form/checkbox';
import radio from 'src/assets/md-theme/components/form/radio';
import autocomplete from 'src/assets/md-theme/components/form/autocomplete';
import flatpickr from 'src/assets/md-theme/components/flatpickr';
import container from 'src/assets/md-theme/components/container';
import popover from 'src/assets/md-theme/components/popover';
import buttonBase from 'src/assets/md-theme/components/buttonBase';
import icon from 'src/assets/md-theme/components/icon';
import svgIcon from 'src/assets/md-theme/components/svgIcon';
import link from 'src/assets/md-theme/components/link';
import dialog from 'src/assets/md-theme/components/dialog';
import dialogTitle from 'src/assets/md-theme/components/dialog/dialogTitle';
import dialogContent from 'src/assets/md-theme/components/dialog/dialogContent';
import dialogContentText from 'src/assets/md-theme/components/dialog/dialogContentText';
import dialogActions from 'src/assets/md-theme/components/dialog/dialogActions';

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...flatpickr,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiTableRow: { ...tableRow },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
});
